'use client'

import Image from 'next/image'
import { cn } from '@/lib/utils'
import { ShoppingBag, ExternalLink } from 'lucide-react'
import { Button } from '@/components/ui/button'

interface MerchBlockContent {
  title: string
  description?: string
  url: string
  imageUrl?: string
}

interface MerchBlockProps {
  content: MerchBlockContent
  className?: string
}

export function MerchBlock({ content, className }: MerchBlockProps) {
  if (!content.url) return null

  const handleClick = () => {
    window.open(content.url, '_blank')
  }

  return (
    <button
      onClick={handleClick}
      className={cn(
        'w-full text-left rounded-xl overflow-hidden',
        'bg-gradient-to-r from-zinc-800/80 to-zinc-800/50',
        'border border-zinc-700/50 hover:border-violet-500/30',
        'transition-all group cursor-pointer',
        className
      )}
    >
      <div className="flex items-stretch">
        {/* Image or icon */}
        <div className="w-24 sm:w-32 flex-shrink-0 bg-zinc-700/50 relative min-h-[96px]">
          {content.imageUrl ? (
            <Image
              src={content.imageUrl}
              alt={content.title}
              fill
              sizes="(max-width: 640px) 96px, 128px"
              className="object-cover"
            />
          ) : (
            <div className="w-full h-full min-h-[96px] flex items-center justify-center">
              <ShoppingBag className="h-8 w-8 text-zinc-500 group-hover:text-violet-400 transition-colors" />
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 p-4 flex flex-col justify-center gap-2">
          <h3 className="font-semibold text-white group-hover:text-violet-400 transition-colors flex items-center gap-2">
            {content.title}
            <ExternalLink className="h-3.5 w-3.5 opacity-50 group-hover:opacity-100" />
          </h3>
          {content.description && (
            <p className="text-sm text-zinc-400 line-clamp-2">{content.description}</p>
          )}
          <Button
            variant="outline"
            size="sm"
            className="w-fit mt-1 border-violet-500/50 text-violet-400 hover:bg-violet-500/10 pointer-events-none"
          >
            <ShoppingBag className="h-4 w-4 mr-1.5" />
            Shop Now
          </Button>
        </div>
      </div>
    </button>
  )
}



